import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy } from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import { ReportScreenshot } from '../../../store/models/reports';
import { SortableImageV2 } from './SortableImageV2';

const ReportsImagesV2 = ({
  title,
  loading,
  reports,
  uploadAndSplitPdf,
  updateReportPositions,
  updateReportInclude,
}: {
  title: string;
  reports: ReportScreenshot[];
  loading: boolean;
  uploadAndSplitPdf?: (params: { file: File; cycleId: string }) => Promise<void>;
  updateReportPositions: (reports: ReportScreenshot[]) => Promise<void>;
  updateReportInclude: (params: { reportId: string; isIncluded: boolean }) => Promise<void>;
}) => {
  const { id: cycleId } = useParams<{ id: string }>();
  const [orderedReports, setOrderedReports] = useState<ReportScreenshot[]>([]);
  const { t } = useTranslation();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    setOrderedReports(reports.sort((a, b) => a.order - b.order));
  }, [reports]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setOrderedReports((items) => {
        const oldIndex = items.findIndex((item) => item.image === active.id);
        const newIndex = items.findIndex((item) => item.image === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex).map((item, index) => ({
          ...item,
          position: index,
        }));

        updateReportPositions(newItems);
        return newItems;
      });
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    const file = event.target.files[0];

    try {
      await uploadAndSplitPdf({ file, cycleId });
    } catch (err) {
      console.error('Error uploading PDF', err);
    }
  };

  const handleIncludeChange = (reportId: string, isIncluded: boolean) => {
    updateReportInclude({ reportId, isIncluded });
  };

  return (
    <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
      <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-black dark:text-white">{title}</h3>
          {uploadAndSplitPdf && (
            <>
              <label
                htmlFor="pdfUpload"
                className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-2.5 text-center font-medium text-white hover:bg-opacity-90 disabled:opacity-50"
              >
                {loading ? t('buttons.uploading') : t('buttons.uploadPdf')}
              </label>
              <input id="pdfUpload" type="file" accept="application/pdf" onChange={handleFileUpload} className="hidden" />
            </>
          )}
        </div>
      </div>

      <div className="p-7">
        {loading ? (
          <div className="flex items-center justify-center p-7">
            <div className="h-16 w-16 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent"></div>
          </div>
        ) : orderedReports.length === 0 ? (
          <div className="text-gray-500 text-center text-sm">{t('labels.noReportsFound')}</div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={orderedReports.map((report) => report.image)} strategy={rectSortingStrategy}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, 1fr)',
                  gap: '16px',
                  width: '100%',
                }}
              >
                {orderedReports.map((report) => (
                  <div key={report.image} className="relative">
                    <div className="absolute top-2 right-2 z-50">
                      <input
                        type="checkbox"
                        defaultChecked={report.isIncluded}
                        onChange={(e) => {
                          handleIncludeChange(report.id, e.target.checked);
                        }}
                        className="h-4 w-4 rounded border-gray-300 cursor-pointer"
                      />
                    </div>
                    <SortableImageV2
                      id={report.image}
                      report={report}
                    />
                  </div>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
};

export default ReportsImagesV2;
